import { useMemo, useCallback } from 'react';
import Ajv from 'ajv';
import addFormats from 'ajv-formats';

// Instanciar Ajv como singleton global fuera del hook
const ajv = new Ajv({ allErrors: true, strict: false });

// Añadir formatos una sola vez
addFormats(ajv);

function formatAjvError(error, value, fieldSchema) {
  const { keyword, params } = error;
  const currentLen = String(value ?? '').length;

  switch (keyword) {
    case 'minLength':
      return `Must be at least ${params.limit} characters (currently ${currentLen})`;
    case 'maxLength':
      return `Must be at most ${params.limit} characters`;
    case 'minimum':
      return `Must be ${params.comparison === '>' ? '>' : '≥'} ${params.limit}`;
    case 'maximum':
      return `Must be ${params.comparison === '<' ? '<' : '≤'} ${params.limit}`;
    case 'pattern':
      return fieldSchema?.patternError ?? 'Does not match the required format';
    case 'format':
      return `Must be a valid ${params.format}`;
    case 'enum':
      return `Must be one of: ${(params.allowedValues ?? []).join(', ')}`;
    case 'required':
      return 'This field is required';
    default:
      return error.message || 'Invalid value';
  }
}

/**
 * Hook que usa ajv para validar el valor de un campo específico contra un JSON Schema.
 * 
 * @param {Object} fieldSchema Schema del campo (e.g. { type: 'string', format: 'email', required: true })
 * @returns {Function} Función `validate(value)` que devuelve null o un mensaje de error como string
 */
export function useAjvValidator(fieldSchema) {
  const validateSchema = useMemo(() => {
    // Si no hay schema o el schema está vacío, retornamos una validación que siempre pasa.
    if (!fieldSchema || Object.keys(fieldSchema).length === 0) {
      return () => true;
    }

    const { required, ...rawFieldSchema } = fieldSchema;

    // Sanitize non-standard types to valid JSON Schema types
    const cleanFieldSchema = { ...rawFieldSchema };
    if (['date', 'datetime', 'date-time', 'email', 'url', 'uri', 'text', 'textarea'].includes(cleanFieldSchema.type)) {
      if (cleanFieldSchema.type === 'date') {
        cleanFieldSchema.format = 'date';
      } else if (cleanFieldSchema.type === 'datetime' || cleanFieldSchema.type === 'date-time') {
        cleanFieldSchema.format = 'date-time';
      } else if (cleanFieldSchema.type === 'email') {
        cleanFieldSchema.format = 'email';
      } else if (cleanFieldSchema.type === 'url' || cleanFieldSchema.type === 'uri') {
        cleanFieldSchema.format = 'uri';
      }
      cleanFieldSchema.type = 'string';
    } else if (cleanFieldSchema.type && !['string', 'number', 'integer', 'boolean', 'array', 'object', 'null'].includes(cleanFieldSchema.type)) {
      // Fallback for any other custom type
      cleanFieldSchema.type = 'string';
    }

    const objectSchema = {
      type: 'object',
      properties: {
        value: cleanFieldSchema
      },
      // Validamos a partir de fieldSchema.required tal cual lo especificado.
      required: fieldSchema.required ? ['value'] : []
    };

    return ajv.compile(objectSchema);
  }, [JSON.stringify(fieldSchema)]);

  const validate = useCallback((value) => {
    const isEmpty = value === '' || value === null || value === undefined;

    // Si el campo no es requerido y el valor es vacío, lo consideramos válido.
    if (!fieldSchema?.required && isEmpty) {
      return null;
    }

    // Envuelve el valor en un objeto { value } tal como espera el schema compilado.
    // Si está vacío y es requerido, pasamos un objeto vacío {} para que AJV dispare el error de "required".
    const dataToValidate = isEmpty ? {} : { value };
    const isValid = validateSchema(dataToValidate);

    if (isValid) {
      return null; // Es válido
    }

    // Si es inválido, extraemos el primer error reportado por ajv
    const errors = validateSchema.errors;
    if (errors && errors.length > 0) {
      const firstError = errors[0];
      if (firstError.keyword === 'required' && firstError.params.missingProperty === 'value') {
        return 'This field is required';
      }
      return formatAjvError(firstError, value, fieldSchema);
    }

    return 'Invalid value';
  }, [validateSchema, fieldSchema]);

  return validate;
}
